(() => {
  'use strict';
  const catalogs = new Map();
  const normalize = value => String(value).toLocaleLowerCase('de').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ß/g, 'ss');
  const loadCatalog = url => {
    if (!catalogs.has(url)) catalogs.set(url, fetch(url).then(r => { if (!r.ok) throw new Error('catalog'); return r.json(); }).catch(error => { catalogs.delete(url); throw error; }));
    return catalogs.get(url);
  };
  class AluEmblemSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.variants = JSON.parse(this.dataset.variants);
      this.color = this.querySelector('[data-emblem-color]');
      this.choice = this.querySelector('[data-emblem-choice]');
      this.status = this.querySelector('[data-emblem-status]');
      this.search = this.querySelector('[data-emblem-search]');
      this.grid = this.querySelector('[data-emblem-grid]');
      this.more = this.querySelector('[data-emblem-more]');
      this.limit = 12;
      this.manualColor = false;
      this.color.addEventListener('change', () => { this.manualColor = true; this.rebuild(); });
      this.choice.addEventListener('change', () => { this.motif = this.choice.selectedOptions[0]?.dataset.motif || ''; this.updateStatus(); this.renderGrid(); this.revealGalleryPreview(); });
      this.search.addEventListener('input', () => { this.limit = 12; this.renderGrid(); });
      this.more.addEventListener('click', () => { this.limit += 24; this.renderGrid(); });
      this.scope = this.closest('[data-pk-type="product"]') || this.closest('.shopify-section') || this.parentElement;
      this.onVariant = event => {
        const id = event.detail?.variant?.id;
        if (id && this.variants[id]) this.setVariant(id);
        else if (event.detail && !event.detail.variant) this.setVariant(null);
      };
      this.scope.addEventListener('variant:changed', this.onVariant);
      this.setVariant(this.dataset.current);
      loadCatalog(this.dataset.catalog).then(data => {
        if (!this.isConnected) return;
        this.catalog = data.sort((a,b) => a.name.localeCompare(b.name,'de') || a.motif.localeCompare(b.motif));
        this.rebuild();
      }).catch(() => { this.status.textContent = 'Die Embleme konnten nicht geladen werden. Bitte laden Sie die Seite erneut.'; });
    }
    disconnectedCallback() { this.scope?.removeEventListener('variant:changed', this.onVariant); this.initialized = false; }
    setVariant(id) {
      const variant = this.variants[id];
      this.size = Number(variant?.size || 0);
      this.hidden = ![25,50].includes(this.size);
      this.choice.disabled = this.hidden;
      this.color.disabled = this.hidden;
      this.choice.required = !this.hidden;
      if (this.hidden) { this.choice.value = ''; this.motif = ''; this.syncGalleryPreview(); return; }
      this.querySelector('[data-emblem-size]').textContent = `– ${this.size} mm`;
      if (!this.manualColor) {
        const title = normalize(variant.title);
        const colors = ['Gold','Silber','Bronze'].filter(c => title.includes(normalize(c)));
        if (colors.length === 1) this.color.value = colors[0];
      }
      if (this.catalog) this.rebuild();
    }
    rebuild() {
      if (!this.catalog || this.hidden) return;
      this.items = this.catalog.flatMap(m => {
        const option = m.options.find(o => o.size === this.size && o.color === this.color.value);
        return option ? [{...m, ...option, value:`${m.name} | Art.-Nr. ${option.sku} | ${option.size} mm | ${option.color}`}]:[];
      });
      this.choice.replaceChildren(new Option('Bitte ein Motiv auswählen',''));
      this.items.forEach(item => {
        const option = new Option(`${item.name} – Nr. ${item.sku}`,item.value);
        option.dataset.motif = item.motif;
        this.choice.add(option);
      });
      const previous = this.items.find(item => item.motif === this.motif);
      this.choice.value = previous?.value || '';
      if (!previous) this.motif = '';
      this.limit = 12;
      this.updateStatus(); this.renderGrid();
    }
    rememberImage(image) {
      if (!this.originalImages) this.originalImages = new Map();
      if (!this.originalImages.has(image)) this.originalImages.set(image, Array.from(image.attributes, a => [a.name,a.value]));
    }
    restoreGalleryPreview() {
      this.originalImages?.forEach((attributes,image) => {
        image.onload = null;
        Array.from(image.attributes).forEach(a => image.removeAttribute(a.name));
        attributes.forEach(([name,value]) => image.setAttribute(name,value));
      });
      this.originalLinks?.forEach((attributes,link) => {
        for (const [name,value] of attributes) value === null ? link.removeAttribute(name) : link.setAttribute(name,value);
      });
      this.scope?.querySelectorAll('[data-emblem-motif-overlay]').forEach(el => el.remove());
      this.scope?.querySelectorAll('[data-emblem-preview-caption]').forEach(el => el.remove());
      this.activePreviewThumb = null;
    }
    syncGalleryPreview() {
      this.restoreGalleryPreview();
      const item = this.items?.find(x => x.value === this.choice.value);
      if (!item || this.hidden) return;
      const slots = Array.from(this.scope.querySelectorAll('[data-emblem-gallery-slot]'));
      const color = normalize(item.color);
      const slot = slots.find(s => normalize(s.dataset.emblemSource).includes('_'+color)) || slots[0];
      if (!slot) return;
      const thumb = Array.from(this.scope.querySelectorAll('[data-emblem-gallery-thumb]')).find(t => t.dataset.mediaId === slot.dataset.mediaId);
      const url = this.dataset.imageBase + item.image;
      // Preserve the original neutral holder and place the motif inside its rim.
      // Both supplied neutral image families share the same circular placement.
      for (const surface of [slot.querySelector('.pk-ratio'), thumb].filter(Boolean)) {
        const overlay = document.createElement('img');
        overlay.dataset.emblemMotifOverlay = '';
        overlay.className = 'alu-emblems__holder-motif';
        overlay.alt = surface === thumb ? '' : `Gewähltes Alu-Emblem: ${item.name}, Artikel ${item.sku}`;
        overlay.src = url;
        overlay.addEventListener('error', () => overlay.remove(), {once:true});
        surface.append(overlay);
      }
      if (thumb) {
        if (!this.originalLinks) this.originalLinks = new Map();
        if (!this.originalLinks.has(thumb)) this.originalLinks.set(thumb,['href','title','aria-label'].map(name=>[name,thumb.getAttribute(name)]));
        thumb.title = `Gewähltes Emblem: ${item.name}`;
        thumb.setAttribute('aria-label',`Gewähltes Emblem ansehen: ${item.name}`);
        this.activePreviewThumb = thumb;
      }
      const caption = document.createElement('p'); caption.dataset.emblemPreviewCaption=''; caption.className='alu-emblems__gallery-caption';
      caption.textContent = `Dein gewähltes Emblem: ${item.value}. Motivvorschau – die Abbildung kann von der gewählten Metallfarbe abweichen.`;
      (slot.closest('.pk-product-gallery__carousel-wrapper') || slot.parentElement).append(caption);
    }
    revealGalleryPreview() {
      if (this.activePreviewThumb) this.activePreviewThumb.click();
    }
    updateStatus() {
      this.status.textContent = this.choice.value ? `Ausgewählt: ${this.choice.value}` : 'Bitte wählen Sie ein Motiv. Die Auswahl wird zusammen mit Ihrer Gravur im Warenkorb gespeichert.';
      this.syncGalleryPreview();
    }
    renderGrid() {
      if (!this.items) return;
      const query = normalize(this.search.value).trim();
      const filtered = this.items.filter(item => normalize(item.name+' '+item.options.map(o=>o.sku).join(' ')).includes(query));
      this.querySelector('[data-emblem-count]').textContent = `${filtered.length} Motive gefunden`;
      this.grid.replaceChildren();
      filtered.slice(0,this.limit).forEach(item => {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'alu-emblems__motif';
        button.setAttribute('aria-pressed',String(item.motif===this.motif));
        button.setAttribute('aria-label',`${item.name}, Artikel ${item.sku}, ${item.color}, ${item.size} mm`);
        if (item.image) {
          const image = document.createElement('img'); image.src = this.dataset.imageBase + item.image; image.alt = ''; image.loading='lazy'; image.width=100; image.height=88; button.append(image);
        } else {
          const fallback = document.createElement('span'); fallback.className='alu-emblems__no-image'; fallback.textContent='Abbildung nicht verfügbar'; button.append(fallback);
        }
        const name = document.createElement('span'); name.textContent=item.name; button.append(name);
        const number = document.createElement('span'); number.textContent=`Nr. ${item.sku}`; button.append(number);
        button.addEventListener('click', () => {
          this.motif=item.motif; this.choice.value=item.value; this.updateStatus(); this.revealGalleryPreview();
          this.grid.querySelectorAll('button').forEach(b => b.setAttribute('aria-pressed',String(b===button)));
        });
        this.grid.append(button);
      });
      this.more.hidden = filtered.length <= this.limit;
    }
  }
  if (!customElements.get('alu-emblem-selector')) customElements.define('alu-emblem-selector',AluEmblemSelector);
})();
